export { TRACK_FOR_GAME } from './tracks.js'
/**
 * What the games can ask to be heard, and what it is heard as.
 *
 * A cue is named for **what happened**, never for a file: a game asks for
 * `answer.right` and never learns which sound that is. Re-scoring the whole
 * app later is then this file and a folder, rather than every screen.
 *
 * Nothing here says anything the screen does not. Every cue below marks a
 * moment already announced in words, in a live region — so muted, the app
 * loses atmosphere and not one fact.
 *
 * And nothing sounds like a verdict. This app has no scores, deliberately, and
 * a wrong answer is met with "Not quite — try again" and two more goes. The
 * cue for that is an invitation, not a buzzer. The biggest sound in the set is
 * `milestone`, which marks games played — turning up, the one number here
 * worth celebrating.
 */

/** A short sound, played over whatever music is going. */
export const CUES = [
  // Shared across the games that ask a question.
  'answer.right',
  'answer.again',
  'answer.moveOn',
  'round.done',
  'milestone',

  // Getting about.
  'move',
  'choose',
  'back',

  // Hoppity's Hangman.
  'letter.right',
  'letter.wrong',
  'word.won',
  'word.lost',

  // Sky's Memory Match.
  'card.flip',
  'pair.found',
  'pair.missed',

  // Small Rabbit's Scramble.
  'tile.take',
  'tile.back',
  'shuffle',

  // Either of the games that will give her a clue.
  'clue',

  // Stripey's Sudoku.
  'clash',
  'grid.clear',

  /*
   * A racer moving along its lane — and deliberately not wired to anything.
   *
   * A racer moves on every right answer, every found pair, every right letter,
   * so this would land on top of another cue every single time it fired and
   * never once on its own. It was named in the plan as the first thing to drop
   * if the screen got busy, and it did.
   *
   * Kept, with its sound, so it is a decision somebody can revisit rather than
   * work they would have to redo.
   */
  'step',
] as const

export type Cue = (typeof CUES)[number]

/** A loop, played under everything until the screen changes. */
export const TRACKS = [
  'menu',
  'hangman',
  'maths',
  'memory',
  'trolley',
  'riddles',
  'scramble',
  'sudoku',
  'room',
] as const

export type Track = (typeof TRACKS)[number]

/**
 * The file each cue is looking for.
 *
 * A `Record`, so a cue added to the list above fails to compile until somebody
 * has decided what it is called. What it cannot do is check the file is
 * *there* — that is the bundler's business, below, and a cue with no file is
 * simply silent.
 */
export const CUE_FILES: Readonly<Record<Cue, string>> = {
  'answer.right': 'answer-right',
  'answer.again': 'answer-again',
  'answer.moveOn': 'answer-moveon',
  'round.done': 'round-done',
  milestone: 'milestone',
  move: 'move',
  choose: 'choose',
  back: 'back',
  'letter.right': 'letter-right',
  'letter.wrong': 'letter-wrong',
  'word.won': 'word-won',
  'word.lost': 'word-lost',
  'card.flip': 'card-flip',
  'pair.found': 'pair-found',
  'pair.missed': 'pair-missed',
  'tile.take': 'tile-take',
  'tile.back': 'tile-back',
  shuffle: 'shuffle',
  clue: 'clue',
  clash: 'clash',
  'grid.clear': 'grid-clear',
  step: 'step',
}

/**
 * Whatever has actually been dropped in.
 *
 * Asked of the bundler at build time rather than guessed at run time, exactly
 * as the game card artwork is — so a file that is not there costs nothing at
 * all, rather than a failed request per cue and a wait for it.
 *
 * Which is what let the whole of this be written and tested before a single
 * sound existed: an empty folder is a silent app, not a broken one.
 */
const FILES = {
  ...import.meta.glob<string>('../assets/sound/sfx/*.mp3', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
  ...import.meta.glob<string>('../assets/sound/music/*.mp3', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
}

/** Where a cue's sound is, or `undefined` if nobody has made one yet. */
export function cueUrl(cue: Cue): string | undefined {
  return FILES[`../assets/sound/sfx/${CUE_FILES[cue]}.mp3`]
}

/** Where a track is, or `undefined` if nobody has made one yet. */
export function trackUrl(track: Track): string | undefined {
  return FILES[`../assets/sound/music/${track}.mp3`]
}

/**
 * Every sound file that exists, by the name it was dropped in as.
 *
 * Only for the test that nothing is sitting in the folder unasked for — a file
 * named `answer_right.mp3` is a file that will never play, and the mistake is
 * invisible from the other direction because a cue with no file is silent by
 * design.
 */
export function filesPresent(): string[] {
  return Object.keys(FILES).map((path) => path.replace('../assets/sound/', ''))
}
